/**
 * Arabic text normalisation for search.
 *
 * Applied at WRITE time into a dedicated `search_text` column, and identically
 * to every incoming query. The index over that column is FTS5 `trigram`, not
 * `unicode61`: FTS5 ships no Arabic stemmer, so عقد / عقود / العقود will never
 * match under a token-based analyser. Trigram substring matching survives
 * Arabic inflection without one. Minimum useful query length is therefore 3.
 *
 * CRITICAL: identifiers are never normalised. Contract/Ejar numbers, CR and VAT
 * numbers, payment references and unit numbers match exactly or by prefix on
 * the RAW value. Revision suffixes, punctuation and leading zeroes are
 * significant — `20974588317` and `20974588317/1-0` are two different leases,
 * and folding them together silently merges two tenancies. See
 * `isIdentifierLike` and the `normalizeIdentifier` counterpart below.
 */

/**
 * Combining marks: tashkeel (fathatan..sukun), superscript alef, tatweel.
 *
 * Written as `\uXXXX` escapes, not as the characters themselves. A combining
 * mark typed literally into a character class attaches itself to the `[` in
 * every editor, diff and code review that renders this file, so the class
 * cannot be read and a wrong edit cannot be seen. Same reason for INVISIBLES
 * below, whose members have no glyph at all.
 */
// Tatweel (\u0640) is listed LAST, after every combining mark. It is the one
// member of this class that is a base letter, and a combining mark written
// straight after it reads as one glyph to any tool that parses the class.
const TASHKEEL_AND_TATWEEL = /[\u064B-\u065F\u0670\u06D6-\u06DC\u0640]/g;

/** Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹) digit blocks. */
const ARABIC_INDIC = /[٠-٩]/g;
const EXT_ARABIC_INDIC = /[۰-۹]/g;

/** Zero-width joiner/non-joiner and bidi control characters. Escaped — see above. */
const INVISIBLES = /[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g;

/**
 * Fold Arabic orthographic variants that users type interchangeably.
 * أ إ آ ٱ ٲ ٳ → ا · ة → ه · ى → ي · ؤ → و · ئ → ي · ک → ك · ی → ي · ھ/ہ → ه
 */
const LETTER_FOLD: Record<string, string> = {
  آ: 'ا', // آ
  أ: 'ا', // أ
  إ: 'ا', // إ
  ٱ: 'ا', // ٱ
  ٲ: 'ا', // ٲ
  ٳ: 'ا', // ٳ
  ة: 'ه', // ة → ه
  ى: 'ي', // ى → ي
  ؤ: 'و', // ؤ → و
  ئ: 'ي', // ئ → ي
  ک: 'ك', // ک (Farsi kaf) → ك
  ی: 'ي', // ی (Farsi yeh) → ي
  ھ: 'ه', // ھ
  ہ: 'ه', // ہ
};

/**
 * EVERY KEY OF `LETTER_FOLD` MUST BE ONE CODE POINT.
 *
 * The keys are concatenated into a character class, and a class matches one
 * code point at a time. A decomposed key — yeh + hamza-above (U+064A U+0654)
 * for ئ — sat in this map and could never match the pair it was written for.
 * What it did instead was contribute its two code points to the class
 * SEPARATELY, so bare ي, among the commonest letters in Arabic, matched the
 * fold regex on every name, title and note in the system and fell through
 * `?? ch` unchanged. Dead weight on every write and every search.
 *
 * It was unnecessary as well as broken: `normalizeArabic` runs `NFKC` first,
 * which composes that pair into U+0626 — the precomposed ئ already in the map.
 */
const LETTER_FOLD_RE = new RegExp(`[${Object.keys(LETTER_FOLD).join('')}]`, 'g');

/**
 * Normalise free text (names, titles, notes, correspondence) for search.
 * Idempotent: `normalizeArabic(normalizeArabic(x)) === normalizeArabic(x)`.
 */
export function normalizeArabic(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .normalize('NFKC')
    .replace(INVISIBLES, '')
    .replace(TASHKEEL_AND_TATWEEL, '')
    .replace(LETTER_FOLD_RE, (ch) => LETTER_FOLD[ch] ?? ch)
    .replace(ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(EXT_ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x06f0))
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Build the `search_text` value for a row by joining every searchable field.
 * Nulls are dropped. Order is irrelevant — trigram matching is positionless.
 */
export function buildSearchText(...parts: Array<string | null | undefined>): string {
  return normalizeArabic(parts.filter(Boolean).join(' '));
}

/**
 * Normalise an IDENTIFIER. Only Arabic-Indic digits are folded to ASCII and
 * whitespace is trimmed — punctuation, case, slashes, dashes and leading zeroes
 * are preserved, because they carry meaning. Use for contract numbers, Ejar
 * numbers, CR/VAT, payment references and unit numbers.
 */
export function normalizeIdentifier(input: string | null | undefined): string {
  if (!input) return '';
  return input
    .normalize('NFKC')
    .replace(INVISIBLES, '')
    .replace(ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(EXT_ARABIC_INDIC, (d) => String(d.charCodeAt(0) - 0x06f0))
    .trim();
}

/** FTS5 trigram needs 3 characters to match anything. */
export const MIN_SEARCH_LENGTH = 3;

/**
 * Escape a user query for an FTS5 MATCH expression. FTS5 treats `"`, `*`, `(`,
 * `)`, `:`, `^`, `-` and `NEAR`/`AND`/`OR`/`NOT` as syntax; wrapping the whole
 * query in double quotes and doubling any embedded quote makes it a literal
 * phrase. This is the FTS5 equivalent of parameter binding — never interpolate
 * a raw query into MATCH.
 */
export function toFtsPhrase(query: string): string {
  return `"${normalizeArabic(query).replace(/"/g, '""')}"`;
}

/** True when the query is long enough for trigram search to return anything. */
export function isSearchable(query: string): boolean {
  return normalizeArabic(query).length >= MIN_SEARCH_LENGTH;
}

/**
 * Heuristic: does this look like an identifier the user is pasting (a contract
 * number, an IBAN fragment, a reference)? Used to decide whether to ALSO run an
 * exact/prefix lookup alongside the trigram search. Never used to decide how to
 * STORE a value.
 */
export function isIdentifierLike(query: string): boolean {
  const q = normalizeIdentifier(query);
  return q.length >= 4 && /^[0-9][0-9\s/\\.\-_]*$/.test(q);
}
